import { useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import MDButton from "components/MDButton";
import PropTypes from "prop-types";
import { Icon } from "@mui/material";
import MDBox from "components/MDBox";
import { useMaterialUIController } from "context";
import { useTheme } from "@emotion/react";

dayjs.extend(customParseFormat);

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function Column({ label, value, min, max, onChange }) {
  const theme = useTheme();
  const prev = value - 1 >= min ? value - 1 : null;
  const next = value + 1 <= max ? value + 1 : null;
  const disabledUp = value <= min;
  const disabledDown = value >= max;
  const dragRef = useRef({
    startY: 0,
    startValue: value,
    applied: 0,
    dragging: false,
  });
  return (
    <Stack
      alignItems="center"
      spacing={1}
      sx={{
        width: 120,
      }}
    >
      <IconButton
        size="small"
        disabled={disabledUp}
        onClick={() => onChange(clamp(value - 1, min, max))}
        sx={{
          opacity: disabledUp ? 0.25 : 0.6,
          color: theme.palette.accent.main,
        }}
      >
        <Icon>keyboard_arrow_up</Icon>
      </IconButton>
      <Stack alignItems="center" spacing={0.5}>
        <Typography
          sx={{ opacity: 0.4, fontWeight: 700 }}
          onClick={() => prev && onChange(prev)}
        >
          {prev ?? ""}
        </Typography>
        <ButtonBase
          disableRipple
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            dragRef.current = {
              startY: e.clientY,
              startValue: value,
              applied: 0,
              dragging: true,
            };
          }}
          onPointerMove={(e) => {
            if (!dragRef.current.dragging) return;
            const diff = dragRef.current.startY - e.clientY;
            const step = Math.trunc(diff / 30);
            if (step !== dragRef.current.applied) {
              dragRef.current.applied = step;
              onChange(clamp(dragRef.current.startValue + step, min, max));
            }
          }}
          onPointerUp={(e) => {
            dragRef.current.dragging = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {}
          }}
          onPointerCancel={(e) => {
            dragRef.current.dragging = false;
            try {
              e.currentTarget.releasePointerCapture(e.pointerId);
            } catch {}
          }}
          onWheel={(e) => {
            if (e.deltaY > 0 && !disabledDown)
              onChange(clamp(value + 1, min, max));
            if (e.deltaY < 0 && !disabledUp)
              onChange(clamp(value - 1, min, max));
          }}
          sx={{
            cursor: "ns-resize",
            userSelect: "none",
          }}
        >
          <Typography
            sx={{
              fontWeight: 900,
              color: theme.palette.text.main,
              fontSize: 40,
            }}
          >
            {String(value).padStart(2, "0")}
          </Typography>
        </ButtonBase>
        <Typography
          sx={{ opacity: 0.4, fontWeight: 700 }}
          onClick={() => next && onChange(next)}
        >
          {next ?? ""}
        </Typography>
      </Stack>
      <IconButton
        size="small"
        disabled={disabledDown}
        onClick={() => onChange(clamp(value + 1, min, max))}
        sx={{
          opacity: disabledDown ? 0.25 : 0.6,
          color: theme.palette.accent.main,
        }}
      >
        <Icon>keyboard_arrow_down</Icon>
      </IconButton>
      <Typography
        variant="caption"
        sx={{
          mt: 0.5,
          fontWeight: 700,
          textTransform: "uppercase",
          color: theme.palette.text.main,
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

export default function StyledDatePicker({
  open,
  onClose,
  value,
  onChange,
  yearStart,
  yearEnd,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const base = value
    ? dayjs(value, ["DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"])
    : dayjs();
  const [year, setYear] = useState(base.year());
  const [month, setMonth] = useState(base.month() + 1);
  const [day, setDay] = useState(base.date());

  const startYear = yearStart ?? base.year() - 10;
  const endYear = yearEnd ?? base.year() + 10;

  const daysInMonth = useMemo(
    () => dayjs(`${year}-${String(month).padStart(2, "0")}-01`).daysInMonth(),
    [year, month]
  );
  const safeDay = clamp(day, 1, daysInMonth);

  const handleYear = (y) => {
    setYear(y);
  };
  const handleMonth = (m) => {
    setMonth(m);
  };
  const handleDay = (d) => {
    setDay(d);
  };

  const selected = useMemo(
    () =>
      dayjs(
        `${year}-${String(month).padStart(2, "0")}-${String(safeDay).padStart(2, "0")}`
      ),
    [year, month, safeDay]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{
        sx: ({ palette }) => ({
          width: 370,
          bgcolor: darkMode
            ? palette.background.fondo
            : palette.background.default,
        }),
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <MDBox
          color="text"
          sx={{
            p: 4,
            borderRadius: 2.5,
            width: 370,
            mx: "auto",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0}
          >
            <Column
              label="Día"
              value={safeDay}
              min={1}
              max={daysInMonth}
              onChange={handleDay}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Column
              label="Mes"
              value={month}
              min={1}
              max={12}
              onChange={handleMonth}
            />
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Column
              label="Año"
              value={year}
              min={startYear}
              max={endYear}
              onChange={handleYear}
            />
          </Stack>
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
            <MDButton
              variant="outlined"
              color="accent"
              onClick={() => onClose?.()}
            >
              Cancelar
            </MDButton>
            <MDButton
              variant="contained"
              color="accent"
              onClick={() => {
                const formatted = selected.format("DD/MM/YYYY");
                onChange?.(formatted);
                onClose?.();
              }}
            >
              Aceptar
            </MDButton>
          </Stack>
        </MDBox>
      </DialogContent>
    </Dialog>
  );
}

StyledDatePicker.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  value: PropTypes.any,
  onChange: PropTypes.func,
  yearStart: PropTypes.number,
  yearEnd: PropTypes.number,
};
